import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App as AntdApp, Card, Table, Input, Select, DatePicker, Tag, Button, Typography, Space, Popconfirm, Popover } from 'antd';
import { DownloadOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import { api } from '../api';
import { t } from '../i18n';
import { HintTooltip as Tooltip } from '../components/HintTooltip';
import { fmtDateTime } from '../time';

type Backup = Awaited<ReturnType<typeof api.listAllBackups>>['items'][number];

const PAGE_SIZE = 30;

// Disk-state capture kinds → human stage + colour. Every source the server can
// write must be listed here — an unlisted one used to render as a raw grey
// string and was missing from the filter (that happened to `uploaded`).
const STAGE: Record<string, { labelKey: string; color: string }> = {
  inventory_snapshot: { labelKey: 'stageReported', color: 'blue' },
  pre_apply: { labelKey: 'stageBefore', color: 'orange' },
  post_apply_snapshot: { labelKey: 'stageAfter', color: 'green' },
  manual: { labelKey: 'stageManual', color: 'purple' },
  uploaded: { labelKey: 'stageUploaded', color: 'cyan' },
  imported: { labelKey: 'stageImported', color: 'geekblue' },
};
const SOURCE_OPTIONS = Object.keys(STAGE);

interface BackupSummary {
  bom?: string;
  level?: string;
  transport?: 'nvme' | 'sas' | string | null;
  formFactor?: '2.5' | '3.5' | null;
  model?: string | null;
  barcode?: string | null;
  manufactured?: string | null;
  description?: string | null;
  boardType?: string | null;
  integrity?: {
    errorCount: number;
    warningCount: number;
    items?: Array<{ level: 'warning' | 'error'; category: string; message: string }>;
  };
}

interface SnGroup {
  sn: string;
  deviceId: string | null;
  lastCreatedAt: number;
  count: number;
  backups: Backup[];
}

/** Transport + form-factor tag (NVMe / SAS 2.5″ / SAS 3.5″) from a parsed summary. */
function diskTypeTag(s: BackupSummary | null) {
  if (!s?.transport) return <Typography.Text type="secondary">—</Typography.Text>;
  if (s.transport === 'nvme') return <Tag color="geekblue">NVMe</Tag>;
  if (s.transport === 'sas')
    return <Tag color={s.formFactor === '3.5' ? 'gold' : 'green'}>{s.formFactor ? `SAS ${s.formFactor}″` : 'SAS'}</Tag>;
  return <Tag>{s.transport}</Tag>;
}

/** Guarded parse so one malformed summary row can't crash the whole page. */
function parseSummaryJson(j: string | null): BackupSummary | null {
  if (!j) return null;
  try {
    return JSON.parse(j) as BackupSummary;
  } catch {
    return null;
  }
}

export function DiskTimeline() {
  const [diskSn, setDiskSn] = useState('');
  const [deviceId, setDeviceId] = useState<string | undefined>();
  const [source, setSource] = useState<string | undefined>();
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [page, setPage] = useState(1);

  const devicesQ = useQuery({ queryKey: ['devices'], queryFn: api.listDevices });
  const meQ = useQuery({ queryKey: ['me'], queryFn: api.me, staleTime: 60_000 });
  const isSuper = meQ.data?.user.role === 'superadmin';
  const qc = useQueryClient();
  const { message } = AntdApp.useApp();
  const del = useMutation({
    mutationFn: (id: string) => api.deleteBackup(id),
    onSuccess: () => {
      message.success(t('backupDeletedOk'));
      qc.invalidateQueries({ queryKey: ['history'] });
    },
    onError: (e: Error) => message.error(e.message),
  });
  const deviceLabel = (id: string | null): string => {
    if (!id) return '—';
    const d = devicesQ.data?.find((x) => x.id === id);
    return d?.label || id.substring(0, 8);
  };

  const fromTs = range?.[0] ? range[0].startOf('day').unix() : undefined;
  const toTs = range?.[1] ? range[1].endOf('day').unix() : undefined;

  // Grouped-by-SN view: fetch a large flat page and group client-side, so each
  // disk collapses to one row (last-backup date + count) that expands to its
  // captures. The filters still apply server-side.
  const q = useQuery({
    queryKey: ['history', diskSn, deviceId ?? '', source ?? '', fromTs ?? '', toTs ?? ''],
    queryFn: () =>
      api.listAllBackups({
        diskSn: diskSn || undefined,
        deviceId,
        source,
        fromTs,
        toTs,
        limit: 2000,
        offset: 0,
      }),
    refetchInterval: 10_000,
  });

  const fmt = fmtDateTime;

  const groups = useMemo<SnGroup[]>(() => {
    const map = new Map<string, SnGroup>();
    for (const b of q.data?.items ?? []) {
      const g =
        map.get(b.diskSn) ??
        { sn: b.diskSn, deviceId: b.deviceId, lastCreatedAt: 0, count: 0, backups: [] };
      g.backups.push(b);
      g.count += 1;
      if (b.createdAt >= g.lastCreatedAt) {
        g.lastCreatedAt = b.createdAt;
        g.deviceId = b.deviceId; // newest capture's device wins
      }
      map.set(b.diskSn, g);
    }
    const arr = [...map.values()];
    for (const g of arr) g.backups.sort((a, b) => b.createdAt - a.createdAt);
    arr.sort((a, b) => b.lastCreatedAt - a.lastCreatedAt); // default: newest first
    return arr;
  }, [q.data]);

  // Per-backup columns (rendered inside an expanded SN group; device + SN live
  // on the group row so they're dropped here).
  const innerColumns: ColumnsType<Backup> = [
    { title: t('backupCreated'), dataIndex: 'createdAt', width: 170, render: (v: number) => <Typography.Text type="secondary">{fmt(v)}</Typography.Text> },
    {
      title: t('historyStage'),
      dataIndex: 'source',
      // A snapshot can carry several stages (byte-identical captures are merged
      // and tagged, not duplicated) — render one tag per stage.
      render: (s: string, row: { alsoSources?: string[] }) => (
        <Space size={4} wrap>
          {[s, ...(row.alsoSources ?? [])].map((st) => {
            const stage = STAGE[st];
            return (
              <Tag key={st} color={stage?.color ?? 'default'}>
                {stage ? t(stage.labelKey) : st}
              </Tag>
            );
          })}
        </Space>
      ),
    },
    {
      title: t('historyState'),
      dataIndex: 'parsedSummaryJson',
      render: (j: string | null) => {
        const s = parseSummaryJson(j);
        if (!s) return '—';
        const integ = s.integrity;
        const hasIssues = integ && (integ.errorCount > 0 || integ.warningCount > 0);
        // Show every field that participates in a flash, one per line.
        const fields: Array<[string, string | null | undefined]> = [
          ['BOM', s.bom],
          ['Level', s.level],
          ['BarCode', s.barcode],
          ['Manufactured', s.manufactured],
          ['BoardType', s.boardType],
          ['Model', s.model],
          ['Description', s.description],
        ];
        const shown = fields.filter(([, v]) => v);
        return (
          <div style={{ fontSize: 12, lineHeight: '17px', maxWidth: 380, wordBreak: 'break-word' }}>
            {shown.length === 0 && <Typography.Text type="secondary">—</Typography.Text>}
            {shown.map(([label, v]) => (
              <div key={label}>
                <Typography.Text type="secondary">{label}:</Typography.Text>{' '}
                <Typography.Text code style={{ fontSize: 11 }}>
                  {v}
                </Typography.Text>
              </div>
            ))}
            {hasIssues &&
              (() => {
                const items = integ.items ?? [];
                const badge = (
                  <Tag
                    color={integ.errorCount > 0 ? 'red' : 'gold'}
                    style={{ cursor: items.length > 0 ? 'pointer' : 'default', marginInlineEnd: 0 }}
                  >
                    {integ.errorCount}e/{integ.warningCount}w
                  </Tag>
                );
                // Clickable details (why "Ne/Nw") when the capture carried them —
                // same interaction as the Disks table integrity badge.
                return (
                  <div style={{ marginTop: 3 }}>
                    {items.length > 0 ? (
                      <Popover
                        trigger="click"
                        placement="rightTop"
                        title={t('diskIntegrityDetailTitle')}
                        content={
                          <div style={{ maxWidth: 380, maxHeight: 300, overflowY: 'auto' }}>
                            {items.map((it, i) => (
                              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 6 }}>
                                <span className={it.level === 'error' ? 'status error' : 'status warn'} style={{ flex: '0 0 auto' }}>
                                  {it.level === 'error' ? t('integrityError') : t('integrityWarning')}
                                </span>
                                <span style={{ fontSize: 12 }}>
                                  <strong>{it.category}:</strong> {it.message}
                                </span>
                              </div>
                            ))}
                          </div>
                        }
                      >
                        {badge}
                      </Popover>
                    ) : (
                      badge
                    )}
                  </div>
                );
              })()}
          </div>
        );
      },
    },
    {
      title: t('historyAppliedBy'),
      dataIndex: 'appliedBy',
      render: (e: string | null, b) =>
        e ? (
          e
        ) : (
          // Automatic device captures have no human applier — show the device
          // that produced this state.
          <Typography.Text type="secondary">
            {deviceLabel(b.deviceId)} · {t('historyAuto')}
          </Typography.Text>
        ),
    },
    {
      title: t('colActions'),
      key: 'actions',
      render: (_: unknown, b) => (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 4 }}>
          <Button size="small" icon={<DownloadOutlined />} href={`/api/admin/v1/disk-backups/${b.id}/download`} download>
            {t('historyDownload')}
          </Button>
          {isSuper && (
            <Popconfirm title={t('backupDeleteConfirm')} okButtonProps={{ danger: true }} onConfirm={() => del.mutate(b.id)}>
              <Button danger size="small" icon={<DeleteOutlined />} loading={del.isPending} block>
                {t('backupDelete')}
              </Button>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ];

  // One row per disk SN. Sortable columns; default sort is by the last-backup
  // date (newest first).
  const outerColumns: ColumnsType<SnGroup> = [
    {
      title: t('historyDiskSn'),
      dataIndex: 'sn',
      render: (sn: string) => <Typography.Text code>{sn}</Typography.Text>,
      sorter: (a, b) => a.sn.localeCompare(b.sn),
    },
    {
      title: t('historyDiskType'),
      key: 'type',
      width: 100,
      render: (_: unknown, g: SnGroup) =>
        diskTypeTag(g.backups.map((b) => parseSummaryJson(b.parsedSummaryJson)).find((s) => s?.transport) ?? null),
    },
    {
      title: t('historyDevice'),
      dataIndex: 'deviceId',
      render: (id: string | null) => <strong>{deviceLabel(id)}</strong>,
    },
    {
      title: t('historyLastBackup'),
      dataIndex: 'lastCreatedAt',
      render: (v: number) => <Typography.Text type="secondary">{fmt(v)}</Typography.Text>,
      sorter: (a, b) => a.lastCreatedAt - b.lastCreatedAt,
      defaultSortOrder: 'descend' as const,
    },
    {
      title: t('historyCount'),
      dataIndex: 'count',
      align: 'right' as const,
      sorter: (a, b) => a.count - b.count,
    },
  ];

  return (
    <Card title={t('navBackups')}>
      <Space wrap style={{ marginBottom: 12 }}>
        <Input
          placeholder={`${t('historyDiskSn')}…`}
          value={diskSn}
          onChange={(e) => { setDiskSn(e.target.value); setPage(1); }}
          style={{ width: 220 }}
          allowClear
        />
        <Select
          style={{ minWidth: 200 }}
          placeholder={t('historyAnyDevice')}
          allowClear
          value={deviceId}
          onChange={(v) => { setDeviceId(v); setPage(1); }}
          options={(devicesQ.data ?? []).map((d) => ({ value: d.id, label: d.label || d.id.substring(0, 8) }))}
        />
        <Select
          style={{ minWidth: 150 }}
          placeholder={t('historyAnySource')}
          allowClear
          value={source}
          onChange={(v) => { setSource(v); setPage(1); }}
          options={SOURCE_OPTIONS.map((s) => ({ value: s, label: t(STAGE[s]!.labelKey) }))}
        />
        <DatePicker.RangePicker
          value={range as [Dayjs, Dayjs] | null}
          onChange={(v) => { setRange(v as [Dayjs | null, Dayjs | null] | null); setPage(1); }}
          allowEmpty={[true, true]}
        />
      </Space>
      <Table<SnGroup>
        size="small"
        rowKey="sn"
        columns={outerColumns}
        dataSource={groups}
        loading={q.isLoading}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: t('emptyList') }}
        expandable={{
          expandedRowRender: (g) => (
            <Table<Backup>
              size="small"
              rowKey="id"
              columns={innerColumns}
              dataSource={g.backups}
              pagination={false}
              scroll={{ x: 'max-content' }}
            />
          ),
        }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: groups.length,
          showSizeChanger: false,
          onChange: setPage,
        }}
      />
    </Card>
  );
}
